export function humanizeError(err: any): string {
    if (!err) return "Unknown error";

    // Handle array of errors recursively
    if (Array.isArray(err)) {
        return err.map(humanizeError).join("; ");
    }

    if (typeof err === "string") return err;

    const message = err.message ?? err.error?.message ?? err.reason?.message;

    if (message) {
        // Check constraints
        const checkSignature = "violates check constraint";
        if (message.includes(checkSignature)) {
            const match = message.match(/violates check constraint "(.+?)"/);
            if (match) {
                const constraintName = match[1];
                if (constraintMessages[constraintName]) {
                    return constraintMessages[constraintName];
                }
            }
        }

        // Not-null constraints
        const notNullSignature = "violates not-null constraint";
        if (message.includes(notNullSignature)) {
            const match = message.match(/null value in column "(.+?)" of relation "(.+?)"/);
            if (match) {
                const [, column, table] = match;
                return `Missing "${column}" in "${table}"`;
            }
        }

        // Foreign key constraints
        const fkSignature = "violates foreign key constraint";
        if (message.includes(fkSignature)) {
            const match = message.match(
                /update or delete on table "(.+?)" violates foreign key constraint "(.+?)" on table "(.+?)"/,
            );
            if (match) {
                const [, sourceTable, constraint, targetTable] = match;
                if (constraintMessages[constraint]) {
                    return constraintMessages[constraint];
                }
                return `Cannot modify "${sourceTable}" because related records exist in "${targetTable}"`;
            }

            // Insert/update foreign key violation
            const insertMatch = message.match(
                /insert or update on table "(.+?)" violates foreign key constraint "(.+?)"/,
            );
            if (insertMatch) {
                const [, targetTable, constraint] = insertMatch;
                if (constraintMessages[constraint]) {
                    return constraintMessages[constraint];
                }
                return `Invalid reference in "${targetTable}" — related record not found`;
            }
        }

        // Unique constraint violations
        const uniqueSignature = "violates unique constraint";
        if (message.includes(uniqueSignature)) {
            const match = message.match(/violates unique constraint "(.+?)"/);
            if (match) {
                const constraintName = match[1];
                if (constraintMessages[constraintName]) {
                    return constraintMessages[constraintName];
                }
                return `Duplicate value violates uniqueness constraint "${constraintName}"`;
            }
        }

        return message;
    }

    try {
        return JSON.stringify(err);
    } catch {
        return String(err);
    }
}

const constraintMessages: Record<string, string> = {
    // capital_account
    capital_account_irr_gross_check: "Check that IRR Gross ≥ -100%",
    capital_account_irr_net_check: "Check that IRR Net ≥ -100%",
    capital_account_tvpi_check: "Check that TVPI ≥ 0",
    capital_account_dpi_check: "Check that DPI ≥ 0",
    capital_account_moic_net_check: "Check that MOIC Net ≥ 0",
    capital_account_moic_gross_check: "Check that MOIC Gross ≥ 0",
    capital_account_beginning_of_financial_year_month_limits: "Check that Beginning of Financial Year Month ≥ 0, ≤ 12",
    capital_account_total_distributions_positive: "Check that Total Distributions ≥ 0",
    capital_account_recalled_distributions_positive: "Check that Recalled / Recallable Distributions ≥ 0",
    capital_account_remaining_commitment_positive: "Check that Remaining Commitment ≥ 0",
    capital_account_reference_date_limits: "Check that Reference Date > 1900-01-01, < current date",
    capital_account_total_commitments_positive: "Check that Total Commitments > 0",
    capital_account_recalled_management_fee_positive: "Check that Recalled / Recallable Mgmt Fee > 0",
    capital_account_moic_net_lower_equal_than_moic_gross: "Check that MOIC Net ≤ MOIC Gross",
    capital_account_reported_currency_required:
        "Check that Reported Currency is provided when you entered dependent values",

    // data_room
    data_room_currency_required: "Check that Currency is provided when you entered dependent values",
    created_for_year_limits: "Check that Created for Year ≥ 1900, ≤ 9999",

    // deal
    deal_board_seats_count_positive: "Check that Board Seats Count ≥ 0",
    deal_total_board_seats_count_positive: "Check that Total Board Seats Count ≥ 0",
    deal_total_board_seats_count_larger_than_board_seats_count: "Check that Total Board Seats > Board Seats",
    deal_exit_date_limits: "Check that Exit Date > Entry Date, < current date",
    deal_entry_date_limit: "Check that Entry Date < current date",
    deal_investors_in_deal_count_positive: "Check that Investors In Deal Count ≥ 0",
    deal_reported_currency_required: "Check that Reported Currency is provided when you entered dependent values",

    // deal_cash_flow
    deal_cash_flow_date_limits: "Check that Cash Flow Date > 1900-01-01, < current date",

    // deal_time_series
    deal_time_series_irr_values_greater_than_negative_one: "Check that IRR ≥ -100%",
    deal_time_series_moic_net_check: "Check that MOIC Net ≥ 0",
    deal_time_series_moic_net_unlevered_check: "Check that MOIC Net Unlevered ≥ 0",
    deal_time_series_moic_gross_check: "Check that MOIC Gross ≥ 0",
    deal_time_series_employees_count_positive: "Check that Employee Count ≥ 1",
    deal_time_series_ebitda_margin_lower_than_100_percent: "Check that EBITDA Margin at t < 100%",
    deal_time_series_revenue_multiple_positive: "Check that Revenue Multiple att ≥ 0",
    deal_time_series_recurring_revenue_positive: "Check that Recurring Revenue at t ≥ 0",
    deal_time_series_recurring_revenue_percentage_positive: "Check that % Recurring Revenue at t ≥ 0",
    deal_time_series_ownership_economic_range: "Check that Ownership Economic at t ≥ 0%, < 100%",
    deal_time_series_total_value_larger_than_realized_value: "Check that Total Value at t ≥ Realized Value at t",
    deal_time_series_total_value_larger_than_unrealized_value: "Check that Total Value at t ≥ Unrealized Value at t",
    deal_time_series_revenue_larger_than_ebitda: "Check that Revnue at t ≥ EBITDA at t",
    deal_time_series_cumulative_addons_positive: "Check that Cumulative Addons at t ≥ 0",
    deal_time_series_capex_positive: "Check that Capex at t ≥ 0",
    deal_time_series_revenue_positive: "Check that Revenue at t ≥ 0",
    deal_time_series_equity_positive: "Check that Equity at t ≥ 0",
    deal_time_series_reported_date_limits: "Check that Reported Date > 1900-01-01, < current date",
    deal_time_series_realised_value_unrealised_value_total_value_100kthreshold:
        "Check that ABS(Realized Value at t + Unrealized Value at t - Total Value at t) ≤ 100000 (rounding threshold)",
    deal_time_series_irr_net_lower_equal_than_irr_gross: "Check that IRR Net at t ≤ IRR Gross at t",
    deal_time_series_moic_net_lower_equal_than_moic_gross: "Check that MOIC Net at t ≤ MOIC Gross at t",
    deal_time_series_management_equity_percentage_check: "Check that Management Equity % at t ≥ 0%, < 100%",
    deal_time_series_reporting_currency_financials_required:
        "Check that Reporting Currency Financials is provided when you entered dependent values",
    deal_time_series_reporting_currency_valuation_required:
        "Check that Reporting Currency Valuation is provided when you entered dependent values",
    deal_time_series_unique_combination:
        "Check that there are no multiple entries with the same combination of reference date, prefix, suffix, and currencies for the same deal",

    // deal_time_series_co_investor
    deal_time_series_co_investor_ownership_percentage_check: "Check that Ownership Economic in % ≥ 0, ≤ 100%",

    // fund
    fund_generation_positive: "Check that Fund Generation > 0",
    fund_target_investment_count_positive: "Check that Target No. of Investments in Fund > 0",
    fund_term_positive: "Check that Term > 0",
    fund_investment_period_years_positive: "Check that Investment Period Years > 0",
    fund_management_fee_investment_period_percentage_check: "Check that Mgmt Fee (Investment Period) in % ≥ 0, ≤ 100%",
    fund_management_fee_liquidation_period_percentage_check: "Check that Mgmt (Liquidation Period) in % ≥ 0, ≤ 100%",
    fund_carried_interest_terms_check: "Check that Carried Interest in % ≥ 0, ≤ 100%",
    fund_hurdle_rate_percentage_check: "Check that Hurdle Rate in % ≥ 0, ≤ 100%",
    fund_vintage_year_limits: "Check that Fund Vintage Year > 1900, ≤ current year",
    fund_financial_year_end_month_limits: "Check that Financial Year End Month ≥ 0, ≤ 12",
    fund_reported_currency_required: "Check that Reported Currency is provided when you entered dependent values",

    // fund_cash_flow
    fund_cash_flow_date_limits: "Check that Cash Flow Date > 1900-01-01, < current date",

    // fund_limited_partner_reported_date_limits
    fund_limited_partner_reported_date_limits: "Check that Cash Flow Date > 1900-01-01, ≤ current date",

    // general_partner_firm
    general_partner_firm_employees_count_positive: "Check that Employees Count > 0",
    general_partner_firm_fund_families_count_positive: "Check that Fund Families Count > 0, < 1000",
    general_partner_firm_reported_currency_required:
        "Check that Reported Currency is provided when you entered dependent values",

    // organization
    organization_founded_date_limit: "Check that Founded Date ≤ current date",
    organization_employees_count_positive: "Check that Employees Count > 0",
};
